"use client";

import type { LessonNotebookState } from "@/hooks/useLessonNotebook";
import type { WhiteboardSessionBoard } from "@/hooks/useWhiteboard";
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { EarlierBoardList, earlierBoards } from "./LessonBoardThread";
import styles from "./LessonNotebook.module.css";

export function LessonNotebook({
  notebook,
  analyzing = false,
  boards = [],
  currentBoardId,
}: {
  notebook: LessonNotebookState;
  analyzing?: boolean;
  /** The lesson's boards: earlier ones are kept here, beside the phrases they taught. */
  boards?: WhiteboardSessionBoard[];
  currentBoardId?: string | null;
}) {
  const headingId = useId();
  const bodyId = useId();
  // Collapsed by default with a count: the current board and the conversation
  // stay the focus; the notebook is one tap away.
  const [hidden, setHidden] = useState(true);
  const { phrases, weakAreas } = notebook;
  const earlier = earlierBoards(boards, currentBoardId);
  const empty = !phrases.length && !weakAreas.length && !earlier.length;
  const count = phrases.length + earlier.length + weakAreas.length;
  return (
    <section aria-labelledby={headingId} className={styles.notebook} data-lesson-notebook>
      <div className={styles.headerRow}>
        <h2 id={headingId} className={styles.title}>
          Notebook
          {count ? <span className={styles.count}> · {count}</span> : null}
        </h2>
        {analyzing ? (
          <span className={styles.muted} role="status">
            Updating…
          </span>
        ) : null}
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={!hidden}
          aria-controls={bodyId}
          aria-label={hidden ? "Show notebook" : "Hide notebook"}
          title={hidden ? "Show notebook" : "Hide notebook"}
          onClick={() => setHidden((value) => !value)}
        >
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={styles.chevron}
            data-open={hidden ? undefined : true}
          />
        </button>
      </div>
      <div id={bodyId} className={styles.body} hidden={hidden}>
        {empty ? (
          <p className={styles.muted}>
            Phrases Chicky teaches and your board practice collect here.
          </p>
        ) : null}
        {phrases.length ? (
          <ul className={styles.list}>
            {[...phrases].reverse().map((phrase) => (
              <li key={phrase.key} className={styles.phrase}>
                <p className={styles.phraseText}>{phrase.text}</p>
                {phrase.translation ? (
                  <p className={styles.translation}>{phrase.translation}</p>
                ) : null}
                {phrase.needsReview || phrase.concepts.length ? (
                  <p className={styles.tags}>
                    {phrase.needsReview ? (
                      <span className={styles.review}>Worth another look</span>
                    ) : null}
                    {phrase.concepts.length ? <span>{phrase.concepts.join(" · ")}</span> : null}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {earlier.length ? (
          <>
            <h3 className={styles.subtitle}>Earlier boards</h3>
            <EarlierBoardList boards={earlier} />
          </>
        ) : null}
        {weakAreas.length ? (
          <>
            <h3 className={styles.subtitle}>Keep practising</h3>
            <ul className={styles.list}>
              {weakAreas.map((area) => (
                <li key={area.concept} className={styles.weak}>
                  <span className={styles.weakConcept}>{area.concept}</span>
                  {area.suggestion ? <span className={styles.muted}>{area.suggestion}</span> : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </section>
  );
}
